import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Upload, Trash2, Save, ImageIcon, Loader2, CheckCircle2, FileText, Download } from "lucide-react";
import AvidaLogo from "@/components/brand/AvidaLogo";
import { TextInput, FieldLabel } from "@/components/wizard/FormKit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LogoPreview({ providerNumber, cacheBuster }) {
  if (!providerNumber) {
    return (
      <div className="flex items-center justify-center h-40 rounded-lg border border-dashed border-[#B7CADA] bg-[#F0F6FC] text-[#3A5275] text-[13px]">
        Enter a provider number above to preview your logo
      </div>
    );
  }
  const src = `${API}/agency/brand/${providerNumber}/logo?${cacheBuster}`;
  return (
    <img
      src={src}
      alt="Agency logo preview"
      className="max-h-40 w-auto bg-white rounded-lg border border-[#D6E3EF] p-4 shadow-sm"
      data-testid="brand-logo-preview"
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
    />
  );
}

export default function AgencyBranding() {
  const [providerNumber, setProviderNumber] = useState("");
  const [record, setRecord] = useState(null);
  const [form, setForm] = useState({
    agency_name: "",
    primary_color: "#0A3D79",
    accent_color: "#FFDA0B",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cacheBuster, setCacheBuster] = useState(Date.now());
  const fileRef = useRef(null);

  // Prefill agency from localStorage (wizard draft)
  useEffect(() => {
    try {
      const draft = JSON.parse(localStorage.getItem("wi-pa-wizard-v1") || "{}");
      const a = draft?.agency || {};
      if (a.provider_number && !providerNumber) setProviderNumber(a.provider_number);
      if (a.name) setForm((f) => ({ ...f, agency_name: a.name }));
    } catch { /* localStorage unavailable (private mode) — non-fatal */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRecord = async (pn) => {
    if (!pn) return;
    try {
      const res = await axios.get(`${API}/agency/brand/${pn}`);
      setRecord(res.data);
      setForm({
        agency_name: res.data.agency_name || "",
        primary_color: res.data.primary_color || "#0A3D79",
        accent_color: res.data.accent_color || "#FFDA0B",
      });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (providerNumber.length >= 5) {
      fetchRecord(providerNumber);
    }
  }, [providerNumber]);

  const handleSave = async () => {
    if (!providerNumber.trim()) {
      toast.error("Provider number is required.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("agency_name", form.agency_name || "");
      fd.append("primary_color", form.primary_color || "");
      fd.append("accent_color", form.accent_color || "");
      if (file) fd.append("logo", file);
      const res = await axios.post(`${API}/agency/brand/${providerNumber}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRecord(res.data);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setCacheBuster(Date.now());
      toast.success(
        file
          ? "Logo uploaded — all future PDFs will use your branding."
          : "Brand settings saved.",
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!providerNumber.trim()) return;
    if (!window.confirm("Remove this agency's branding? PDFs will revert to the Avida default.")) return;
    try {
      await axios.delete(`${API}/agency/brand/${providerNumber}`);
      setRecord(null);
      setFile(null);
      setCacheBuster(Date.now());
      setForm({ agency_name: "", primary_color: "#0A3D79", accent_color: "#FFDA0B" });
      toast.success("Branding removed. Using Avida default.");
    } catch (err) {
      toast.error("Delete failed.");
    }
  };

  return (
    <main className="min-h-screen bg-paper">
      <div className="brand-strip-thin border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
            <div className="rounded-lg bg-white p-1 border border-white/20">
              <AvidaLogo size="xs" iconOnly />
            </div>
            <div className="pl-3 border-l border-white/20">
              <div className="font-display text-[14px] font-semibold text-white">Avida Health</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#FFDA0B] font-bold mt-0.5">Agency Branding</div>
            </div>
          </Link>
          <Link to="/" className="btn-ghost" data-testid="brand-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
        <section className="card-surface">
          <h1 className="font-display text-3xl font-semibold text-[#0B1E3A] mb-2 tracking-tight">
            Agency <span className="yellow-underline">Branding</span>
          </h1>
          <p className="text-[14px] text-[#3A5275] max-w-2xl mb-6">
            Upload your agency's logo and brand colors. Every generated Plan of Care and
            Non-Skilled Assessment PDF will carry your branding in the top-left letterhead.
            If nothing is uploaded, PDFs use the Avida default.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <TextInput
                label="Agency Provider Number"
                value={providerNumber}
                onChange={setProviderNumber}
                placeholder="e.g. 1234567"
                required
                testid="brand-provider-number"
              />
              <TextInput
                label="Agency Display Name (shown on PDFs)"
                value={form.agency_name}
                onChange={(v) => setForm((f) => ({ ...f, agency_name: v }))}
                placeholder="Avida Personal Care"
                testid="brand-agency-name"
              />
              <div>
                <FieldLabel>Logo Upload</FieldLabel>
                <div className="flex items-center gap-3">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="hidden"
                    data-testid="brand-file-input"
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="btn-ghost"
                    data-testid="brand-file-pick"
                  >
                    <Upload className="w-4 h-4" /> Choose logo…
                  </button>
                  <span className="text-[12.5px] text-[#3A5275]">
                    {file ? file.name : "PNG / JPG / WEBP · max 2 MB"}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <TextInput
                  label="Primary Color (navy)"
                  value={form.primary_color}
                  onChange={(v) => setForm((f) => ({ ...f, primary_color: v }))}
                  placeholder="#0A3D79"
                  testid="brand-primary-color"
                />
                <TextInput
                  label="Accent Color (yellow)"
                  value={form.accent_color}
                  onChange={(v) => setForm((f) => ({ ...f, accent_color: v }))}
                  placeholder="#FFDA0B"
                  testid="brand-accent-color"
                />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={busy || !providerNumber}
                  className="btn-primary"
                  data-testid="brand-save"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {busy ? "Saving…" : "Save branding"}
                </button>
                {record?.logo_exists && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="btn-ghost"
                    data-testid="brand-delete"
                  >
                    <Trash2 className="w-4 h-4" /> Remove
                  </button>
                )}
              </div>
              {record && record.provider_number !== "_default" && (
                <div className="flex items-center gap-2 text-[12.5px] text-[#1FA0E6]" data-testid="brand-status">
                  <CheckCircle2 className="w-4 h-4" />
                  Custom branding active for provider {record.provider_number}.
                </div>
              )}
            </div>

            <div className="space-y-4">
              <FieldLabel>Logo preview</FieldLabel>
              <LogoPreview providerNumber={providerNumber} cacheBuster={cacheBuster} />
              <div className="rounded-lg border border-[#D6E3EF] bg-white p-4 shadow-sm">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#3A5275] mb-3">
                  PDF letterhead preview
                </div>
                <div className="border border-[#D6E3EF] rounded-md bg-white">
                  <div className="flex items-center justify-between px-4 py-3 border-b-2 border-[#0A3D79]">
                    {providerNumber ? (
                      <img
                        src={`${API}/agency/brand/${providerNumber}/logo?${cacheBuster}`}
                        alt="Logo"
                        className="h-9 w-auto"
                      />
                    ) : (
                      <ImageIcon className="w-9 h-9 text-[#B7CADA]" />
                    )}
                    <div className="text-right">
                      <div className="text-[11px] font-bold text-[#0A3D79] tracking-wide">
                        PLAN OF CARE
                      </div>
                      <div className="text-[10px] text-[#3A5275]">
                        {form.agency_name || "Avida Personal Care"}
                      </div>
                    </div>
                  </div>
                  <div className="px-4 py-6 text-[11px] text-[#3A5275]">
                    Client, diagnoses, medications, PCW orders, signatures…
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="card-surface mt-6" data-testid="integration-docs-section">
          <h2 className="font-display text-xl font-semibold text-[#0B1E3A] mb-1 tracking-tight">
            Integration documents
          </h2>
          <p className="text-[13px] text-[#3A5275] mb-5">
            Latest copy of the docs prepared for the parent-app team. Download
            and forward to your account contacts as needed — these regenerate
            from the source markdown each time the app boots.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <a
              href={`${API}/docs/integration-handoff-bundle.pdf`}
              download="avida_integration_handoff_bundle.pdf"
              className="flex items-start gap-3 rounded-lg border border-[#D6E3EF] bg-white p-4 hover:border-[#1565B8] hover:bg-[#F0F6FC] transition-colors"
              data-testid="open-handoff-bundle"
            >
              <div className="shrink-0 w-10 h-10 rounded-md bg-[#FFFDEC] border border-[#F0E29A] flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#8A6B00]" />
              </div>
              <div className="flex-1">
                <div className="text-[14px] font-semibold text-[#0B1E3A]">
                  Integration handoff bundle
                </div>
                <div className="text-[12px] text-[#3A5275] mt-0.5">
                  Prior-visit schema · BAA initiation · runbook · IR template (~21 pages)
                </div>
                <div className="flex items-center gap-1 text-[12px] text-[#1565B8] font-semibold mt-2">
                  <Download className="w-3.5 h-3.5" /> Download PDF
                </div>
              </div>
            </a>
            <a
              href={`${API}/docs/parent-app-questions.pdf`}
              download="parent_app_integration_questions.pdf"
              className="flex items-start gap-3 rounded-lg border border-[#D6E3EF] bg-white p-4 hover:border-[#1565B8] hover:bg-[#F0F6FC] transition-colors"
              data-testid="open-parent-questions"
            >
              <div className="shrink-0 w-10 h-10 rounded-md bg-[#E7F1FB] border border-[#B7CADA] flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#0A3D79]" />
              </div>
              <div className="flex-1">
                <div className="text-[14px] font-semibold text-[#0B1E3A]">
                  Original integration questionnaire
                </div>
                <div className="text-[12px] text-[#3A5275] mt-0.5">
                  Feb-25-2026 questions sent to the parent team (now resolved)
                </div>
                <div className="flex items-center gap-1 text-[12px] text-[#1565B8] font-semibold mt-2">
                  <Download className="w-3.5 h-3.5" /> Download PDF
                </div>
              </div>
            </a>
          </div>
        </section>
      </div>
    </main>
  );
}
